import React, { useState } from 'react';

import { useTOTP } from '../hooks/useTOTP';

interface TOTPSetupProps {
  onSetupComplete?: () => void;
  onVerifyComplete?: () => void;
  onCancel?: () => void;
}

export const TOTPSetup: React.FC<TOTPSetupProps> = ({
  onSetupComplete,
  onVerifyComplete,
  onCancel,
}) => {
  const [step, setStep] = useState<'setup' | 'verify'>('setup');
  const [totpCode, setTotpCode] = useState('');
  const { isLoading, error, setupResult, verifyResult, setupTOTP, verifyTOTP, resetState } =
    useTOTP();

  const handleSetup = async () => {
    try {
      await setupTOTP();
      setStep('verify');
      if (onSetupComplete) {
        onSetupComplete();
      }
    } catch (err) {
      console.error('Error setting up TOTP:', err);
    }
  };

  const handleVerify = async () => {
    if (!totpCode) return;

    try {
      const result = await verifyTOTP(totpCode);
      if (result.status === 'SUCCESS') {
        if (onVerifyComplete) {
          onVerifyComplete();
        }
      }
    } catch (err) {
      console.error('Error verifying TOTP:', err);
    }
  };

  const handleCancel = () => {
    resetState();
    if (onCancel) {
      onCancel();
    }
  };

  const renderSetupStep = () => {
    if (!setupResult) {
      return (
        <div className="totp-setup-container">
          <h2>Set Up Two-Factor Authentication</h2>
          <p>Enhance your account security with two-factor authentication.</p>
          <button onClick={handleSetup} disabled={isLoading}>
            {isLoading ? 'Setting up...' : 'Set Up TOTP'}
          </button>
          <button onClick={handleCancel}>Cancel</button>
          {error && <div className="error-message">{error.message}</div>}
        </div>
      );
    }

    return (
      <div className="totp-setup-container">
        <h2>Scan QR Code</h2>
        <p>
          Scan this QR code with your authenticator app (like Google Authenticator, Authy, or
          Microsoft Authenticator).
        </p>

        <img src={setupResult.qrCodeUrl} alt="TOTP QR Code" style={{ width: 200, height: 200 }} />

        <p>If you can't scan the QR code, enter this code manually in your authenticator app:</p>
        <div className="secret-code">{setupResult.secretCode}</div>

        <button onClick={() => setStep('verify')}>Continue</button>
        <button onClick={handleCancel}>Cancel</button>
      </div>
    );
  };

  const renderVerifyStep = () => (
    <div className="totp-verify-container">
      <h2>Verify Authentication Code</h2>
      <p>Enter the 6-digit code from your authenticator app to verify the setup.</p>

      <input
        type="text"
        value={totpCode}
        onChange={(e) => setTotpCode(e.target.value)}
        placeholder="Enter 6-digit code"
        maxLength={6}
        style={{ fontSize: 18, padding: 8, textAlign: 'center' }}
      />

      <button onClick={handleVerify} disabled={isLoading || totpCode.length !== 6}>
        {isLoading ? 'Verifying...' : 'Verify Code'}
      </button>
      <button onClick={handleCancel}>Cancel</button>

      {error && <div className="error-message">{error.message}</div>}
      {verifyResult && verifyResult.status === 'SUCCESS' && (
        <div className="success-message">
          Two-factor authentication has been successfully set up!
        </div>
      )}
    </div>
  );

  return step === 'setup' ? renderSetupStep() : renderVerifyStep();
};

export const TOTPSetupNative = TOTPSetup;
